import type { OAuth2FormValues } from '#layers/access/utils/auth-providers/protocols/oauth2';
import type { FormSchemaDataField, FormSchemaResolved } from '#layers/block-engine/public';
import {

	isFormSchemaDataField,
	isFormSchemaPresentationalEntry,
	isFormSchemaSectionEntry,
	mapFormSchemaEntries,
} from '#layers/block-engine/public';

export const OIDC_SCOPES = ['openid', 'email', 'profile'] as const;
export const OIDC_CLAIM_MAPPINGS = { sub: 'sub', email: 'email' } as const;

export interface OAuth2PresetDefaults {
	authorizationEndpoint: string;
	tokenEndpoint: string;
	userinfoEndpoint: string;
	scopes: readonly string[];
	claimMappings: { sub: string; email?: string };
	extraAuthorizeParams?: Record<string, string>;
}

interface ApplyOAuth2PresetOptions {
	defaults?: Record<string, unknown>;
	descriptions?: Record<string, string>;
	omitFieldNames?: readonly string[];
	omitSectionLabels?: readonly string[];
	fieldOverrides?: Record<string, Record<string, unknown>>;
}

export function oauth2PresetDefaults(defaults: OAuth2PresetDefaults): Record<string, unknown> {
	const mappedDefaults: Record<string, unknown> = {
		'authorizationEndpoint': defaults.authorizationEndpoint,
		'tokenEndpoint': defaults.tokenEndpoint,
		'userinfoEndpoint': defaults.userinfoEndpoint,
		'scopes': defaults.scopes,
		'claimMappings.sub': defaults.claimMappings.sub,
		'extraAuthorizeParams': Object.entries(defaults.extraAuthorizeParams ?? {})
			.map(([key, value]) => ({ key, value })),
	};

	if (defaults.claimMappings.email) mappedDefaults['claimMappings.email'] = defaults.claimMappings.email;

	return mappedDefaults;
}

export function applyOAuth2PresetOptions(
	items: FormSchemaResolved,
	options: ApplyOAuth2PresetOptions,
): FormSchemaResolved {
	const omitFieldNames = new Set(options.omitFieldNames);
	const omitSectionLabels = new Set(options.omitSectionLabels);

	return mapFormSchemaEntries(items, (item) => {
		const itemLabel = getPresentationalLabel(item);

		if (itemLabel && omitSectionLabels.has(itemLabel)) {
			return;
		}

		if (isFormSchemaSectionEntry(item)) {
			return {
				...item,
				props: {
					...item.props,
					defaultOpen: false,
				},
			};
		}

		if (isFormSchemaDataField(item)) {
			return applyOAuth2PresetFieldOptions(item, options, omitFieldNames);
		}

		return item;
	});
}

function applyOAuth2PresetFieldOptions(
	item: FormSchemaDataField<Record<string, unknown>, string>,
	options: ApplyOAuth2PresetOptions,
	omitFieldNames: Set<string>,
): FormSchemaDataField<Record<string, unknown>, string> | undefined {
	const itemName = item.name;

	if (omitFieldNames.has(itemName)) return;

	let field = item;

	if (options.defaults && itemName in options.defaults) {
		field = {
			...field,
			default: () => options.defaults![itemName],
		};
	}

	if (options.descriptions && itemName in options.descriptions) {
		field = {
			...field,
			description: options.descriptions[itemName]!,
		};
	}

	if (options.fieldOverrides && itemName in options.fieldOverrides) {
		field = { ...field, ...options.fieldOverrides[itemName] };
	}

	return field;
}

export function promoteOAuth2PresetFields(
	items: FormSchemaResolved,
	fieldNames: readonly string[],
): FormSchemaResolved {
	const names = new Set(fieldNames);
	const promoted = new Map<string, FormSchemaResolved[number]>();

	const remaining = removePromotedFields(items, names, promoted);
	const primaryFields = fieldNames.flatMap((name) => promoted.get(name) ?? []);
	const firstGroupIndex = remaining.findIndex((item) => isFormSchemaSectionEntry(item));

	if (firstGroupIndex === -1) return [...remaining, ...primaryFields];

	return [
		...remaining.slice(0, firstGroupIndex),
		...primaryFields,
		...remaining.slice(firstGroupIndex),
	];
}

export function promoteOAuth2PresetFieldsBeforeCredentials(
	items: FormSchemaResolved,
	fieldNames: readonly string[],
): FormSchemaResolved {
	const names = new Set(fieldNames);
	const promoted = new Map<string, FormSchemaResolved[number]>();
	const remaining = removePromotedFields(items, names, promoted);
	const primaryFields = fieldNames.flatMap((name) => promoted.get(name) ?? []);

	return [
		...primaryFields,
		...remaining,
	];
}

function removePromotedFields(
	items: FormSchemaResolved,
	names: Set<string>,
	promoted: Map<string, FormSchemaResolved[number]>,
): FormSchemaResolved {
	return mapFormSchemaEntries(items, (item) => {
		if ('name' in item && item.name && names.has(item.name)) {
			promoted.set(item.name, item);
			return;
		}

		return item;
	});
}

/**
 * Read the omit-matching label from presentational entries only. Sections
 * sharing an omitted label keep their nested fields, matching the legacy
 * separator-only omit behavior.
 */
function getPresentationalLabel(item: FormSchemaResolved[number]): string | null {
	if (!isFormSchemaPresentationalEntry(item)) {
		return null;
	}

	const label = item.props?.label;

	return typeof label === 'string' ? label : null;
}

export function normalizeDomain(value: string, suffix?: string): string {
	const domain = value
		.trim()
		.replace(/^https?:\/\//, '')
		.replace(/\/.*$/, '');

	if (!suffix || domain.endsWith(suffix) || domain.includes('.')) return domain;

	return `${domain}${suffix}`;
}

/**
 * Restores an OAuth2 preset's domain field from its saved authorization URL.
 *
 * If the URL is absent, non-string, or malformed, keeps the base protocol's
 * parsed values so editing legacy configuration cannot fail.
 */
export function parseOAuth2PresetAuthorizationHostname(
	config: unknown,
	parsed: Partial<OAuth2FormValues>,
): Partial<OAuth2FormValues> {
	const authorizationEndpoint = (config as { authorizationEndpoint?: unknown } | null | undefined)
		?.authorizationEndpoint;

	if (typeof authorizationEndpoint !== 'string') return parsed;

	try {
		return {
			...parsed,
			authorizationEndpoint: new URL(authorizationEndpoint).hostname,
		};
	}
	catch {
		return parsed;
	}
}
